import React, { useEffect, useState } from 'react'
import { getSiteSettings } from '@/lib/api'
import { SiteSettings } from '@/types'
import { cn } from '@/lib/utils'

interface BlockchainProfilesProps {
  className?: string
  iconSize?: 'sm' | 'md' | 'lg'
}

const BlockchainProfiles: React.FC<BlockchainProfilesProps> = ({ className, iconSize = 'md' }) => {
  const [siteSettings, setSiteSettings] = useState<Partial<SiteSettings> | null>(null)

  // Fetch site settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await getSiteSettings()
        setSiteSettings(settings)
      } catch (error) {
        console.error('Failed to fetch site settings:', error)
      }
    }

    fetchSettings()
  }, [])

  // Render blockchain profile icon based on network
  const renderBlockchainIcon = (network: string) => {
    const sizeClass = {
      sm: 'h-4 w-4',
      md: 'h-6 w-6',
      lg: 'h-8 w-8',
    }[iconSize]

    switch (network) {
      case 'ethereum':
        return (
          <svg className={sizeClass} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M11.944 17.97L4.58 13.62 11.943 24l7.37-10.38-7.372 4.35h.003zM12.056 0L4.69 12.223l7.365 4.354 7.365-4.35L12.056 0z" />
          </svg>
        )
      case 'polygon':
        return (
          <svg className={sizeClass} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 0l12 12-12 12L0 12 12 0zM8.5 11.5L12 8l3.5 3.5L12 15l-3.5-3.5z" />
          </svg>
        )
      case 'bsc':
        return (
          <svg className={sizeClass} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 0l2.7 2.7-6.5 6.5-2.7-2.7L12 0zm0 24l-2.7-2.7 6.5-6.5 2.7 2.7L12 24zm0-8.9l-6.5-6.5 2.7-2.7 3.8 3.8 3.8-3.8 2.7 2.7-6.5 6.5z" />
          </svg>
        )
      case 'solana':
        return (
          <svg className={sizeClass} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M17.16 10.87a.71.71 0 00-.5-.2H4.24c-.43 0-.64.51-.34.8l2.43 2.42c.13.13.3.2.5.2h12.41c.43 0 .64-.51.34-.8zm0-5.07a.71.71 0 00-.5-.2H4.24c-.43 0-.64.51-.34.8l2.43 2.42c.13.13.3.2.5.2h12.41c.43 0 .64-.51.34-.8zm-2.93 10.14H4.24c-.43 0-.64.51-.34.8l2.43 2.42c.13.13.3.2.5.2h12.41c.43 0 .64-.51.34-.8l-2.43-2.42a.71.71 0 00-.5-.2z" />
          </svg>
        )
      case 'ens':
        return (
          <svg className={sizeClass} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
          </svg>
        )
      default:
        return (
          <svg className={sizeClass} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-6h2v2h-2v-2zm0-8h2v6h-2V6z" />
          </svg>
        )
    }
  }

  if (!siteSettings?.blockchainProfiles || siteSettings.blockchainProfiles.length === 0) {
    return null
  }

  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {siteSettings.blockchainProfiles.map(profile => (
        <a
          key={profile.network}
          href={profile.url || '#'}
          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 transition-colors"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="mr-1">{renderBlockchainIcon(profile.network)}</span>
          <span>{profile.displayName || profile.identifier}</span>
        </a>
      ))}
    </div>
  )
}

export default BlockchainProfiles
